import { call, put, select } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';
import { get, getOr, omit } from 'lodash/fp';
import * as Api from '../../../services/fbm-api';
import { sagaRoutineRequest } from '../../../helpers/saga';
import * as routines from './routines';
import { formatDateRange } from '../../../helpers/dates';
import redirectTo from '../../../helpers/redirectTo';
import history from '../../../routes/history';
import { parseInt } from '../../../helpers/utils';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import {
  guestsBasePath,
  guestsViewPath,
  reservationsViewPath
} from '../../../routes/Paths';
import { getIsHouseholdDetails } from '../../Settings/selectors/main';
import {
  guestViewAddMember,
  guestViewUpdateMember
} from '../actions/routes/view';
import { updateGuestReservationSearchQuery } from '../actions/components/reservation-search';

export const createGuest = ({ payload: { body, redirectPath = '' } }) =>
  sagaRoutineRequest(routines.createGuest, {
    request: () => call(Api.createClient, { body }),
    *success({ id }) {
      yield toastr.success('Success', 'Client successfully created.');
      if (redirectPath) {
        redirectTo(redirectPath);
      } else {
        const isHouseholdDetails = yield select(getIsHouseholdDetails);
        const hasMembers = parseInt(getOr(1, 'household_total', body)) > 1;
        const section =
          isHouseholdDetails && hasMembers ? 'members?isNew=1' : 'primary';
        history.push(pagePathToUrl(guestsViewPath, { id, section }));
      }
    }
  });

export const updateGuest = ({ payload: { id, body, section } }) =>
  sagaRoutineRequest(routines.updateGuest, {
    request: () => call(Api.updateClient, { id, body, section }),
    *success() {
      yield toastr.success('Success', 'Client successfully updated.');
    }
  });

export const mergeGuest = ({ payload: { id, sourceId } }) =>
  sagaRoutineRequest(routines.mergeGuest, {
    request: () => call(Api.mergeClient, { id, sourceId }),
    *success() {
      yield toastr.success('Success', 'Guests successfully merged.');
      history.push(pagePathToUrl(guestsViewPath, { id, section: 'primary' }));
    }
  });

export const fetchGuestForView = ({ payload: id }) =>
  sagaRoutineRequest(routines.fetchGuestForView, {
    request: () => call(Api.fetchClient, id)
  });

export const fetchGuestForMerge = ({ payload: id }) =>
  sagaRoutineRequest(routines.fetchGuestForMerge, {
    request: () => call(Api.fetchClient, id)
  });

export const createHouseholdMember = ({ payload: { guestId, body } }) =>
  sagaRoutineRequest(routines.createHouseholdMember, {
    request: () => call(Api.createHouseholdMember, { guestId, body }),
    *success(response) {
      yield put(guestViewAddMember(response));
      yield toastr.success('Success', 'Member successfully added.');
    }
  });

export const updateHouseholdMember = ({ payload: { id, guestId, body } }) =>
  sagaRoutineRequest(routines.updateHouseholdMember, {
    request: () => call(Api.updateHouseholdMember, { id, guestId, body }),
    *success(response) {
      yield put(guestViewUpdateMember(response));
      yield toastr.success('Success', 'Member successfully updated.');
    }
  });

export const deleteHouseholdMember = ({ payload: { id, guestId } }) =>
  sagaRoutineRequest(routines.deleteHouseholdMember, {
    request: () => call(Api.deleteHouseholdMember, { id, guestId }),
    successPayload: { id },
    *success() {
      yield toastr.success('Success', 'Member successfully removed.');
    }
  });

export const fetchGuestRelationship = ({ payload: id }) =>
  sagaRoutineRequest(routines.fetchGuestRelationship, {
    request: () => call(Api.fetchClient, id)
  });

export const fetchRelatedGuestsForField = ({ payload: { guestId, field } }) =>
  sagaRoutineRequest(routines.fetchRelatedGuestsForField, {
    request: () =>
      call(Api.fetchClientRelatedGuestsForField, {
        guestId,
        guestRelationField: field
      })
  });

export const fetchGuestAuditLogs = ({ payload: { guestId, page } }) =>
  sagaRoutineRequest(routines.fetchGuestAuditLogs, {
    request: () => call(Api.fetchGuestAuditLogs, { guestId, page })
  });

export const fetchGuestNotes = ({ payload: { guestId, page } }) =>
  sagaRoutineRequest(routines.fetchGuestNotes, {
    request: () => call(Api.fetchGuestNotes, { guestId, page })
  });

export const createGuestNote = ({ payload: { objId, roleId, note } }) =>
  sagaRoutineRequest(routines.createGuestNote, {
    request: () =>
      call(Api.createNote, {
        body: { objType: 'Guest', objId, roleId, note }
      }),
    *success() {
      yield toastr.success('Success', `Note successfully created.`);
    }
  });

export const updateGuestNote = ({ payload: { id, body } }) =>
  sagaRoutineRequest(routines.updateGuestNote, {
    request: () => call(Api.updateNote, { id, body }),
    *success() {
      yield toastr.success('Success', `Note successfully updated.`);
    }
  });

export const deleteGuestNote = ({ payload: id }) =>
  sagaRoutineRequest(routines.deleteGuestNote, {
    request: () => call(Api.deleteNote, id),
    successPayload: { id },
    *success() {
      yield toastr.success('Success', `Note successfully deleted.`);
    }
  });

export const fetchGuestFiles = ({ payload: query }) =>
  sagaRoutineRequest(routines.fetchGuestFiles, {
    request: () => call(Api.fetchGuestFiles, query)
  });

export const createGuestFile = ({ payload: body }) =>
  sagaRoutineRequest(routines.createGuestFile, {
    request: () =>
      call(Api.createFile, {
        body: { ...body, objType: 'guest' }
      }),
    *success() {
      yield toastr.success('Success', `File successfully created.`);
    }
  });

export const deleteGuestFile = ({ payload: id }) =>
  sagaRoutineRequest(routines.deleteGuestFile, {
    request: () => call(Api.deleteFile, id),
    successPayload: { id },
    *success() {
      yield toastr.success('Success', `File successfully deleted.`);
    }
  });

export const fetchGuestVisits = ({ payload: query }) =>
  sagaRoutineRequest(routines.fetchGuestVisits, {
    request: () => {
      const visitOn = formatDateRange(getOr([], 'visit_on', query));
      return call(Api.fetchGuestVisits, { ...query, visit_on: visitOn });
    }
  });

export const fetchGuestVisitTrack = ({ payload: { id, visitTrackId } }) =>
  sagaRoutineRequest(routines.fetchGuestVisitTrack, {
    request: () => call(Api.fetchVisitTrack, { id, visitTrackId })
  });

export const fetchGuestReservations = ({ payload: { guestId, query } }) =>
  sagaRoutineRequest(routines.fetchGuestReservations, {
    request: () => call(Api.fetchGuestReservations, { guestId, query }),
    *success({ items: reservations }) {
      const reservation = reservations[0];
      if (get('code', query) && reservation) {
        yield put(updateGuestReservationSearchQuery(omit('code', query)));
        history.push(
          pagePathToUrl(reservationsViewPath, {
            id: get('id', reservation)
          })
        );
      }
    }
  });

export const fetchUserRoleOptions = () =>
  sagaRoutineRequest(routines.fetchUserRoleOptions, {
    request: () => call(Api.fetchUserRoleOptions, {})
  });

export const fetchUserLocationOptions = () =>
  sagaRoutineRequest(routines.fetchUserLocationOptions, {
    request: () => call(Api.fetchUserLocationOptions, {})
  });

export const fetchAssociatedDomainOptions = () =>
  sagaRoutineRequest(routines.fetchAssociatedDomainOptions, {
    request: () => call(Api.fetchAssociatedDomainOptions, {})
  });

export const fetchAssociationLinkOptions = () =>
  sagaRoutineRequest(routines.fetchAssociationLinkOptions, {
    request: () => call(Api.fetchAssociationLinkOptions, {})
  });

export const deleteGuest = ({ payload: id }) =>
  sagaRoutineRequest(routines.deleteGuest, {
    request: () => call(Api.deleteGuest, id),
    successPayload: { id },
    *success() {
      yield toastr.success('Success', `Guest successfully deleted.`);
      history.push(pagePathToUrl(guestsBasePath));
    }
  });
